/**
 * brot-abo-price.js
 *
 * Dynamically updates the per-box price anchor on the Brot-Abo product page.
 * Subscribes to Dawn's PUB_SUB_EVENTS.variantChange event so it reacts
 * whenever the customer selects a different variant.
 *
 * Depends on:
 *   - pubsub.js  (subscribe / PUB_SUB_EVENTS — loaded by Dawn's main-product section)
 *   - constants.js (PUB_SUB_EVENTS.variantChange = 'variant-change')
 *
 * DOM contract (rendered by snippets/brot-abo-price.liquid):
 *   <brot-abo-price data-section-id="…">
 *     <span id="brot-abo-per-box-price-{sectionId}">…</span>
 *     <span id="brot-abo-per-bread-price-{sectionId}">…</span>
 *     <span id="brot-abo-total-price-{sectionId}">…</span>
 *   </brot-abo-price>
 */

/**
 * Format a price in cents to a locale string using Shopify's money format.
 * Falls back to a simple "€X,XX" format (German locale) when the Shopify
 * global money_format is unavailable.
 *
 * @param {number} cents - Price in cents (integer)
 * @returns {string}
 */
function formatMoney(cents) {
  // Shopify exposes window.Shopify.money_format, e.g. "{{amount_with_comma_separator}} €"
  // We do a simple substitution to avoid a full moneyjs dependency.
  const amount = (cents / 100).toFixed(2).replace('.', ',');
  const fmt = (window.Shopify && window.Shopify.money_format) || '{{amount_with_comma_separator}} €';
  return fmt
    .replace('{{amount_with_comma_separator}}', amount)
    .replace('{{amount}}', (cents / 100).toFixed(2))
    .replace('{{amount_no_decimals}}', Math.floor(cents / 100).toString())
    .replace('{{amount_with_comma_separator_and_currency_code}}', amount + ' EUR');
}

/**
 * Parse the number of boxes from an option value string.
 * Expected formats: "3 Brot-Boxen", "6 Brot-Boxen", "12 Brot-Boxen".
 * Returns the leading integer, or 1 as a safe fallback.
 *
 * @param {string} optionValue
 * @returns {number}
 */
function parseBoxCount(optionValue) {
  if (!optionValue) return 1;
  const match = String(optionValue).match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 1;
}

/**
 * Parse the number of breads per box from an option value string.
 * Expected formats: "Klein – 4 Brote", "Mittel – 6 Brote", "Groß – 8 Brote".
 * Extracts the trailing integer before "Brote", or returns 1 as a safe fallback.
 *
 * @param {string} optionValue
 * @returns {number}
 */
function parseBreadCount(optionValue) {
  if (!optionValue) return 1;
  const match = String(optionValue).match(/(\d+)\s*Brote?\s*$/i);
  return match ? parseInt(match[1], 10) : 1;
}

class BrotAboPrice extends HTMLElement {
  connectedCallback() {
    this.sectionId   = this.dataset.sectionId;
    this.perBoxEl    = document.getElementById(`brot-abo-per-box-price-${this.sectionId}`);
    this.perBreadEl  = document.getElementById(`brot-abo-per-bread-price-${this.sectionId}`);
    this.totalEl     = document.getElementById(`brot-abo-total-price-${this.sectionId}`);

    // Subscribe to Dawn's variant change pub/sub event
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== this.sectionId) return;
        this.handleVariantChange(event.data.variant);
      });
    }
  }

  disconnectedCallback() {
    if (typeof this._unsubscribe === 'function') {
      this._unsubscribe();
    }
  }

  /**
   * @param {Object} variant - Shopify variant object from Dawn pub/sub event.
   *   variant.price   {number}   — price in cents
   *   variant.options {string[]} — option values array; index 0 = size/bread-count, index 1 = box count
   */
  handleVariantChange(variant) {
    if (!variant) return;

    const totalCents = variant.price;
    // option2 is index 1 of the options array (box count)
    const option2Value = variant.options && variant.options[1];
    const boxCount = parseBoxCount(option2Value);
    const perBoxCents = boxCount > 0 ? Math.round(totalCents / boxCount) : totalCents;

    // option1 is index 0 of the options array (size label, contains bread count)
    const option1Value = variant.options && variant.options[0];
    const breadCount = parseBreadCount(option1Value);
    const perBreadCents = (boxCount > 0 && breadCount > 0)
      ? Math.round(totalCents / boxCount / breadCount)
      : perBoxCents;

    if (this.perBoxEl)   this.perBoxEl.textContent   = formatMoney(perBoxCents);
    if (this.perBreadEl) this.perBreadEl.textContent  = formatMoney(perBreadCents);
    if (this.totalEl)    this.totalEl.textContent     = formatMoney(totalCents);
  }
}

customElements.define('brot-abo-price', BrotAboPrice);
